import { useEffect, useMemo, useRef, type CSSProperties, type RefObject } from 'react';

import {
  DATATABLE_PAGINATED_HEIGHT_CAP,
  DEFAULT_CLIENT_PAGE_SIZE,
} from './constants.js';
import type { DataTableDensity } from './dataTableCell.js';
import type { DataTableLayoutKind, DataTablePagination } from './types.js';

function paginationFitViewport(pagination: DataTablePagination): boolean {
  return pagination.fitViewport !== false;
}

/** Pure page-count math — testable without DOM measurement */
export function dataTablePageCount(options: {
  total: number;
  pageSize: number;
  pageCount?: number;
}): number {
  if (options.pageCount != null) return Math.max(1, options.pageCount);
  return Math.max(1, Math.ceil(options.total / Math.max(1, options.pageSize)) || 1);
}

export function dataTableSafePageIndex(pageIndex: number, pageCount: number): number {
  return Math.min(pageIndex, Math.max(0, pageCount - 1));
}

export function dataTableEffectivePageSize(options: {
  serverPageSize?: number | null;
  clientPageSize?: number | null;
}): number {
  return options.serverPageSize ?? options.clientPageSize ?? DEFAULT_CLIENT_PAGE_SIZE;
}

export function dataTableSliceClientRows<T>(options: {
  data: T[];
  pageIndex: number;
  pageSize: number;
}): T[] {
  const start = Math.max(0, options.pageIndex) * Math.max(1, options.pageSize);
  return options.data.slice(start, start + Math.max(1, options.pageSize));
}

/**
 * List pagination. `fitViewport` is layout-only (shell fill + body scroll) —
 * does not mutate API/pageSize from row-height measurement (Hamssah model).
 */
export function useDataTablePagination<T>(options: {
  layout: DataTableLayoutKind;
  data: T[];
  pagination: DataTablePagination | false | undefined;
  heightCap: string;
  maxBodyHeight?: string;
  tableRef: RefObject<HTMLTableElement | null>;
  density: DataTableDensity;
  stackedLayout: boolean;
  showQuickCreateRow: boolean;
  hasFilterHeader: boolean;
}) {
  const paginationFooterRef = useRef<HTMLDivElement>(null);
  const paginatedShellRef = useRef<HTMLDivElement>(null);
  const tableBodyRegionRef = useRef<HTMLDivElement>(null);
  const stackedListRegionRef = useRef<HTMLDivElement>(null);

  const isList = options.layout.kind === 'list';
  const showFooter = isList && options.pagination !== false;
  const clientPag =
    showFooter && options.pagination && options.pagination.mode === 'client' ? options.pagination : null;
  const serverPag =
    showFooter && options.pagination && options.pagination.mode === 'server' ? options.pagination : null;

  const fitViewport =
    isList &&
    showFooter &&
    options.pagination !== false &&
    options.pagination != null &&
    paginationFitViewport(options.pagination);

  const effectivePageSize = dataTableEffectivePageSize({
    serverPageSize: serverPag?.pageSize,
    clientPageSize: clientPag?.pageSize,
  });

  const pageIndex = clientPag?.pageIndex ?? serverPag?.pageIndex ?? 0;

  const total = serverPag?.total ?? options.data.length;
  const pageCount = useMemo(() => {
    if (!showFooter) return 1;
    if (serverPag) {
      return dataTablePageCount({
        total,
        pageSize: effectivePageSize,
        pageCount: serverPag.pageCount,
      });
    }
    if (clientPag) {
      return dataTablePageCount({ total, pageSize: effectivePageSize });
    }
    return 1;
  }, [showFooter, serverPag, clientPag, total, effectivePageSize]);

  const safePage = dataTableSafePageIndex(pageIndex, pageCount);

  useEffect(() => {
    if (!clientPag) return;
    const max = Math.max(0, pageCount - 1);
    if (clientPag.pageIndex > max) {
      clientPag.onPageIndexChange(max);
    }
  }, [clientPag, pageCount]);

  const rowSource = useMemo(() => {
    if (!showFooter || serverPag) return options.data;
    if (!clientPag) return options.data;
    return dataTableSliceClientRows({
      data: options.data,
      pageIndex: safePage,
      pageSize: effectivePageSize,
    });
  }, [showFooter, serverPag, clientPag, options.data, safePage, effectivePageSize]);

  const shellPaginatedStyle: CSSProperties | undefined = isList
    ? fitViewport
      ? {
          height: '100%',
          minHeight: 0,
          maxHeight: options.heightCap,
        }
      : { maxHeight: options.heightCap }
    : undefined;

  const onPrev = () => {
    const pag = options.pagination;
    if (!showFooter || pag === false || !pag) return;
    pag.onPageIndexChange(Math.max(0, safePage - 1));
  };

  const onNext = () => {
    const pag = options.pagination;
    if (!showFooter || pag === false || !pag) return;
    pag.onPageIndexChange(Math.min(pageCount - 1, safePage + 1));
  };

  /** Server lists without viewport fit: shrink body to row content. */
  const shrinkWrapViewportBody = Boolean(serverPag && !fitViewport && options.data.length > 0);

  return {
    showFooter,
    serverPaginated: Boolean(serverPag),
    shrinkWrapViewportBody,
    rowSource,
    total,
    safePage,
    pageCount,
    effectivePageSize,
    paginationFooterRef,
    paginatedShellRef,
    tableBodyRegionRef,
    stackedListRegionRef,
    shellPaginatedStyle,
    shellPaperStyle: undefined as CSSProperties | undefined,
    layoutMode: isList ? ('paginated' as const) : ('embedded' as const),
    fitViewport,
    onPrev,
    onNext,
  };
}

export function defaultListHeightCap(maxBodyHeight?: string): string {
  return maxBodyHeight ?? DATATABLE_PAGINATED_HEIGHT_CAP;
}
